<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { messageStamp } from 'shared/helpers/timeHelper';
import Button from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import SingleSelect from 'dashboard/components-next/filter/inputs/SingleSelect.vue';

const props = defineProps({
  space: {
    type: Object,
    required: true,
  },
  venues: {
    type: Array,
    default: () => [],
  },
  venueBuzzById: {
    type: Object,
    default: () => ({}),
  },
  loading: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['edit', 'delete', 'update-venue', 'edit-venue']);

const { t } = useI18n();

const isNumericCapacity = val => val != null && val !== '' && val !== 'n/a';

const readableDate = date => {
  if (!date) return '';
  const timestamp = new Date(date).getTime() / 1000;
  return messageStamp(timestamp, 'LLL d, yyyy');
};

const maxCapacity = computed(() => {
  const maxFields = [
    props.space.max_capacity_seated,
    props.space.max_capacity_seated_furn,
    props.space.max_capacity_standing,
  ]
    .filter(isNumericCapacity)
    .map(Number);

  if (maxFields.length > 0) {
    return Math.max(...maxFields);
  }

  const layoutCapacities = [
    props.space.theater,
    props.space.classroom,
    props.space.banquet,
    props.space.boardroom,
    props.space.u_shape,
    props.space.concert,
    props.space.cocktail,
  ]
    .filter(isNumericCapacity)
    .map(Number);

  return layoutCapacities.length > 0 ? Math.max(...layoutCapacities) : null;
});

const capacityConfigurations = computed(() => {
  const configs = [];
  if (isNumericCapacity(props.space.max_capacity_standing))
    configs.push({
      label: t('SETTINGS.SPACES.MAX_CAPACITY_STANDING'),
      value: props.space.max_capacity_standing,
      icon: 'i-lucide-users',
    });
  if (isNumericCapacity(props.space.max_capacity_seated_furn))
    configs.push({
      label: t('SETTINGS.SPACES.MAX_CAPACITY_SEATED_FURN'),
      value: props.space.max_capacity_seated_furn,
      icon: 'i-lucide-armchair',
    });
  if (isNumericCapacity(props.space.max_capacity_seated))
    configs.push({
      label: t('SETTINGS.SPACES.MAX_CAPACITY_SEATED_FREE'),
      value: props.space.max_capacity_seated,
      icon: 'i-lucide-theater',
    });
  if (isNumericCapacity(props.space.theater))
    configs.push({
      label: t('SETTINGS.SPACES.THEATER'),
      value: props.space.theater,
      icon: 'i-lucide-presentation',
    });
  if (isNumericCapacity(props.space.classroom))
    configs.push({
      label: t('SETTINGS.SPACES.CLASSROOM'),
      value: props.space.classroom,
      icon: 'i-lucide-graduation-cap',
    });
  if (isNumericCapacity(props.space.banquet))
    configs.push({
      label: t('SETTINGS.SPACES.BANQUET'),
      value: props.space.banquet,
      icon: 'i-lucide-utensils',
    });
  if (isNumericCapacity(props.space.boardroom))
    configs.push({
      label: t('SETTINGS.SPACES.BOARDROOM'),
      value: props.space.boardroom,
      icon: 'i-lucide-briefcase',
    });
  if (isNumericCapacity(props.space.u_shape))
    configs.push({
      label: t('SETTINGS.SPACES.U_SHAPE'),
      value: props.space.u_shape,
      icon: 'i-lucide-move',
    });
  if (isNumericCapacity(props.space.concert))
    configs.push({
      label: t('SETTINGS.SPACES.CONCERT'),
      value: props.space.concert,
      icon: 'i-lucide-spotlight',
    });
  if (isNumericCapacity(props.space.cocktail))
    configs.push({
      label: t('SETTINGS.SPACES.COCKTAIL'),
      value: props.space.cocktail,
      icon: 'i-lucide-wine',
    });
  return configs;
});

const isChild = computed(() => !!props.space.parent_id);

const parentName = computed(() => {
  if (!props.space.parent_id) return null;
  return props.space.parent?.name || null;
});

const hasConfigurations = computed(
  () => capacityConfigurations.value.length > 0
);

const hasAnyProperty = computed(
  () =>
    hasConfigurations.value || props.space.square_meters || maxCapacity.value
);

const venueOptions = computed(() =>
  props.venues.map(v => ({ id: v.id, name: v.name }))
);

const selectedVenueRecord = computed(() => {
  if (!props.space.venue_id) return null;
  return props.venues.find(venue => venue.id === props.space.venue_id) || null;
});

const selectedVenue = computed(() => {
  if (!selectedVenueRecord.value) return null;
  return {
    id: selectedVenueRecord.value.id,
    name: selectedVenueRecord.value.name,
  };
});

const venueBuzzScore = computed(() => {
  if (!props.space.venue_id) return null;
  return props.venueBuzzById[props.space.venue_id];
});

const onVenueChange = val => {
  emit('update-venue', props.space, val?.id || null);
};

const clearVenue = () => {
  emit('update-venue', props.space, null);
};
</script>

<template>
  <tr>
    <td class="py-4 ltr:pr-4 rtl:pl-4 min-w-[200px]">
      <div class="flex items-center gap-3" :class="{ 'pl-4': isChild }">
        <div
          class="shrink-0 size-10 rounded-md overflow-hidden bg-n-slate-3 flex items-center justify-center"
        >
          <img
            v-if="space.media_url"
            :src="space.media_url"
            :alt="space.name"
            class="size-full object-cover"
          />
          <Icon v-else icon="i-lucide-image" class="size-4 text-n-slate-9" />
        </div>
        <span>
          <span v-if="isChild" class="text-n-slate-9 mr-1">└─</span>
          {{ space.name }}
        </span>
      </div>
    </td>
    <td class="py-4 ltr:pr-4 rtl:pl-4 min-w-[140px]">
      <span v-if="parentName" class="text-sm text-n-slate-11">{{
        parentName
      }}</span>
      <span v-else class="text-n-slate-9 text-sm">—</span>
    </td>
    <td class="py-4 ltr:pr-4 rtl:pl-4 min-w-[160px]">
      <div class="flex items-center gap-2">
        <SingleSelect
          :model-value="selectedVenue"
          :options="venueOptions"
          :placeholder="t('SETTINGS.SPACES.ASSIGN_VENUE')"
          @update:model-value="onVenueChange"
        />
        <Button
          v-if="selectedVenueRecord"
          v-tooltip.top="t('SETTINGS.SPACES.VENUES.EDIT')"
          icon="i-lucide-building-2"
          slate
          faded
          sm
          @click="$emit('edit-venue', selectedVenueRecord, $event)"
        />
        <span
          v-if="venueBuzzScore != null"
          v-tooltip.top="t('SETTINGS.SPACES.VENUES.BUZZ.SCORE')"
          class="inline-flex items-center rounded-full bg-n-brand/10 px-2 py-0.5 text-xs font-medium text-n-brand"
        >
          {{ venueBuzzScore }}
        </span>
        <Button
          v-if="selectedVenue"
          v-tooltip.top="t('SETTINGS.SPACES.CLEAR_VENUE')"
          icon="i-lucide-x"
          slate
          faded
          sm
          @click="clearVenue"
        />
      </div>
    </td>
    <td class="py-4 ltr:pr-4 rtl:pl-4 max-w-md min-w-[200px]">
      <div class="flex flex-wrap gap-1.5">
        <div
          v-if="space.square_meters"
          v-tooltip.top="t('SETTINGS.SPACES.SQUARE_METERS')"
          class="inline-flex items-center gap-1.5 px-2 py-1 rounded-full bg-n-slate-3 text-n-slate-11 text-xs"
        >
          <Icon icon="i-lucide-ruler" size="xs" />
          <span>{{ space.square_meters }} m²</span>
        </div>
        <div
          v-if="maxCapacity"
          v-tooltip.top="t('SETTINGS.SPACES.MAX_CAPACITY')"
          class="inline-flex items-center gap-1.5 px-2 py-1 rounded-full bg-n-slate-3 text-n-slate-11 text-xs font-medium"
        >
          <Icon icon="i-lucide-maximize" size="xs" />
          <span>{{ maxCapacity }}</span>
        </div>
        <div
          v-for="config in capacityConfigurations"
          :key="config.label"
          v-tooltip.top="config.label"
          class="inline-flex items-center gap-1.5 px-2 py-1 rounded-full bg-n-slate-3 text-n-slate-11 text-xs"
        >
          <Icon :icon="config.icon" size="xs" />
          <span>{{ config.value }}</span>
        </div>
        <span v-if="!hasAnyProperty" class="text-n-slate-9 text-sm">—</span>
      </div>
    </td>
    <td class="py-4 ltr:pr-4 rtl:pl-4 min-w-[120px]">
      {{ readableDate(space.created_at) }}
    </td>
    <td class="py-4 min-w-xs">
      <div class="flex gap-1 justify-end flex-shrink-0">
        <Button
          v-tooltip.top="$t('SETTINGS.SPACES.EDIT')"
          icon="i-lucide-pen-line"
          slate
          md
          faded
          :is-loading="loading"
          @click="$emit('edit', space)"
        />
        <Button
          v-tooltip.top="$t('SETTINGS.SPACES.DELETE')"
          :is-loading="loading"
          icon="i-lucide-trash-2"
          md
          ruby
          faded
          @click="$emit('delete', space)"
        />
      </div>
    </td>
  </tr>
</template>
